/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for verifying a drug shipment.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugShipmentVerifyState {
    drugShipmentVerify: Dtos.DrugShipmentVerify | null;
    verifyState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugShipmentVerifyState {
    drugShipmentVerify: IInitialDrugShipmentVerifyState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentVerifyInitialState: IInitialDrugShipmentVerifyState = {
    drugShipmentVerify: null,
    verifyState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentVerifySelector: TypedUseSelectorHook<IDrugShipmentVerifyState> = useSelector;

export const DrugShipmentVerifySelectors = {
    selectDrugShipmentVerify: (state: IDrugShipmentVerifyState) => state.drugShipmentVerify.drugShipmentVerify,
    selectVerifyState: (state: IDrugShipmentVerifyState) => state.drugShipmentVerify.verifyState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugShipmentVerifyReducer extends ImmerReducer<IInitialDrugShipmentVerifyState> {
    public verify(form: Dtos.DrugShipmentVerify) {
        this.draftState.verifyState = {
            state: RequestState.Pending
        };
    }

    public verifySuccess(drugShipmentVerify?: Dtos.DrugShipmentVerify) {
        this.draftState.drugShipmentVerify = drugShipmentVerify ? drugShipmentVerify : null;
        this.draftState.verifyState = {
            state: RequestState.Success
        };
    }

    public verifyFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugShipmentVerify = null;
        this.draftState.verifyState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugShipmentVerifyInitialState };
    }
};

export const DrugShipmentVerifyActions = createActionCreators(DrugShipmentVerifyReducer);
const reducer = createReducerFunction(DrugShipmentVerifyReducer, DrugShipmentVerifyInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugShipmentVerifyApi = {
    verify: (drugShipmentVerify?: Dtos.DrugShipmentVerify) => {
        return client.post(new Dtos.SetDrugShipmentVerify({
            drugShipmentVerify
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugShipmentVerifyLogic = {
    verify: createLogic<IDrugShipmentVerifyState, {}, undefined, string, ReturnType<typeof DrugShipmentVerifyActions.verify>>({
        type: DrugShipmentVerifyActions.verify.type,
        process: async ({ action }, dispatch, done) => {
            const drugShipmentVerify = action.payload;

            try {
                const response = await DrugShipmentVerifyApi.verify(drugShipmentVerify);

                dispatch(DrugShipmentVerifyActions.verifySuccess(
                    response.drugShipmentVerify
                ));
            }
            catch (error: any) {
                dispatch(DrugShipmentVerifyActions.verifyFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DrugShipmentVerifyLogic.verify
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugShipmentVerify = (): [Dtos.DrugShipmentVerify | null,
    IRequestState<Dtos.ResponseStatus>,
    typeof DrugShipmentVerifyActions] => {
    const drugShipmentVerify = useSelector(DrugShipmentVerifySelectors.selectDrugShipmentVerify);
    const verifyState = useSelector(DrugShipmentVerifySelectors.selectVerifyState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugShipmentVerifyActions>(() => {
        return bindActionCreators(DrugShipmentVerifyActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugShipmentVerifyActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugShipmentVerify,
        verifyState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugShipmentVerify', reducer as any, logics as any);